import { createFileRoute } from '@tanstack/react-router'
import { type ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { BusinessCard, BusinessCardStatusType } from '@/api/admin-companies-types'
import { preloadListBusinessCards, useListBusinessCards } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { SearchField } from '@/components/base/search-field'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { SingleColumnPage } from '@/components/page-layout'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table, useServerSort } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { paginatedSearchWithSort } from '@/utils/route-utilities'
export const Route = createFileRoute('/_authenticated/_general/business-cards/')({
	component: BusinessCardsPage,
	validateSearch: paginatedSearchWithSort(['name', 'cardNumber', 'status', 'externalId']),
	loaderDeps: ({ search: { limit, offset, q, sortBy, sortOrder } }) => ({
		limit,
		offset,
		q,
		sortBy,
		sortOrder
	}),
	loader: async ({ context, deps: { limit, offset, q, sortBy, sortOrder } }) =>
		await preloadListBusinessCards(context, {
			query: {
				limit,
				offset,
				q,
				sortBy,
				sortOrder
			}
		}),
	staticData: {
		crumb: ({ context: { L } }) => L.BusinessCards.Title()
	}
})

function BusinessCardsPage() {
	const { L } = useI18n()
	const { limit = DEFAULT_PAGE_SIZE, offset, q, sortBy, sortOrder } = Route.useSearch()
	const serverSort = useServerSort(['name', 'cardNumber', 'status', 'externalId'])

	const cards = useListBusinessCards({
		limit,
		offset,
		q,
		sortBy,
		sortOrder
	})

	const columnDefs = useMemo<ColDef<BusinessCard>[]>(
		() => [
			{
				headerName: L.BusinessCards.Column.Name(),
				field: 'name',
				minWidth: 150,
				flex: 2
			},
			{
				headerName: L.BusinessCards.Column.CardNumber(),
				field: 'cardNumber',
				minWidth: 140,
				flex: 2
			},
			{
				headerName: L.BusinessCards.Column.Status(),
				field: 'status',
				minWidth: 90,
				flex: 1,
				cellRenderer: ({ value }: { value: BusinessCardStatusType }) => (
					<StatusCell color={getCardStatusColor(value)}>{L.Enum.CardStatus(value)}</StatusCell>
				)
			},
			{
				headerName: L.BusinessCards.Column.ExternalId(),
				field: 'externalId',
				minWidth: 100,
				flex: 1,
				valueGetter: ({ data }) => data?.externalId || '-'
			}
		],
		[L]
	)

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={L.BusinessCards.Title()} rightSide={<SearchField />} />
				<div className='min-w-0 flex-1 overflow-x-auto'>
					<Table<BusinessCard>
						className='w-full min-w-[600px] flex-1'
						data={cards.data?.cards}
						columnUtilities={{}}
						columnDefs={columnDefs}
						serverSort={serverSort}
						animateRows
						suppressCellFocus
						rowToRouterConfig={card => ({
							to: '/business-cards/$cardUuid',
							params: { cardUuid: card.uuid }
						})}
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={cards.data?.count} />
				</div>
			</Container>
		</SingleColumnPage>
	)
}

const getCardStatusColor = (status: BusinessCardStatusType): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'INACTIVE':
			return 'grey'
		case 'BLOCKED':
			return 'orange'
		default:
			return 'grey'
	}
}
